import React, { useState, useRef, useEffect } from "react";
import { useNewsGenerate } from "./NewsGenerateContext";
import TagMultiSelect from "components/TagMultiSelect";
import { generateHTML } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapImage from '@tiptap/extension-image';
import TipTap from "components/TipTap";

const ReviewStep: React.FC = () => {
  const { newsArticle, setNewsArticle } = useNewsGenerate();
  const htmlRef = useRef<HTMLDivElement>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<any>(null);

  // Render content using Tiptap when newsArticle changes
  useEffect(() => {
    if (htmlRef.current && newsArticle && newsArticle.content) {
      // Include the Image extension when generating HTML
      htmlRef.current.innerHTML = generateHTML(newsArticle.content, [
        StarterKit,
        TiptapImage.configure({
          inline: true,
          allowBase64: true,
        }),
      ]);
    }
  }, [newsArticle]);

  const startEditing = (fieldName: string, currentValue: any) => {
    setEditingField(fieldName);
    setEditValue(currentValue);
  };

  const saveEdit = () => {
    if (editingField && editValue !== null && newsArticle) {
      setNewsArticle((prevNews) => ({
        ...prevNews,
        [editingField]: editValue
      }));
      setEditingField(null);
      setEditValue(null);
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue(null);
  };

  if (!newsArticle) {
    return (
      <div className="p-4 border rounded-lg shadow-sm bg-white">
        <p className="text-gray-600">No news article has been generated yet.</p>
      </div>
    );
  }

  const handleTagsChange = (tags: any) => {
    setNewsArticle(prev => ({
      ...prev,
      tags: tags
    }));
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-3 gap-6">
        {/* Article preview */}
        <div className="col-span-1 p-4 bg-white">
          <h2 className="text-xl font-semibold mb-4">Preview</h2>

          {/* Image section - click to upload/change */}
          <div
            className="relative aspect-video mb-4 cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => alert("Image upload functionality would go here")}
          >
            {newsArticle.defaultImage ? (
              <img
                src={newsArticle.defaultImage.url}
                alt={newsArticle.title}
                className="object-cover w-full h-full rounded-lg"
              />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded-lg">
                <span className="text-gray-400">Click to add image</span>
              </div>
            )}
          </div>

          {/* Tags section */}
          <div className="mt-4 mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
            <TagMultiSelect
              selectedTags={newsArticle.tags || []}
              setSelectedTags={handleTagsChange}
              type={["news"]}
            />
          </div>

          {/* Author info */}
          <div className="mt-4 flex items-center text-gray-500">
            {newsArticle?.author?.name && (
              <div className="flex items-center">
                {newsArticle.author?.defaultImage?.url && (
                  <img
                    src={newsArticle.author.defaultImage.url}
                    alt={newsArticle.author.name}
                    className="w-8 h-8 rounded-full mr-2"
                  />
                )}
                <span>{newsArticle.author.name}</span>
              </div>
            )}
          </div>

          {/* Status, Featured, Trending toggles */}
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setNewsArticle(prev => ({
                      ...prev,
                      status: "published"
                    }));
                  }}
                  className={`px-3 py-1 text-sm rounded-lg ${newsArticle.status === "published"
                    ? "bg-green-600 text-white"
                    : "bg-gray-200 text-gray-700"}`}
                >
                  Published
                </button>
                <button
                  onClick={() => {
                    setNewsArticle(prev => ({
                      ...prev,
                      status: "pending"
                    }));
                  }}
                  className={`px-3 py-1 text-sm rounded-lg ${newsArticle.status === "pending"
                    ? "bg-yellow-500 text-white"
                    : "bg-gray-200 text-gray-700"}`}
                >
                  Pending
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    setNewsArticle(prev => ({
                      ...prev,
                      featured: !prev.featured
                    }));
                  }}
                  className={`px-3 py-1 text-sm rounded-lg w-full text-left ${newsArticle.featured
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700"}`}
                >
                  {newsArticle.featured ? "✓ Featured" : "☐ Featured"}
                </button>

                <button
                  onClick={() => {
                    setNewsArticle(prev => ({
                      ...prev,
                      trending: !prev.trending
                    }));
                  }}
                  className={`px-3 py-1 text-sm rounded-lg w-full text-left ${newsArticle.trending
                    ? "bg-purple-600 text-white"
                    : "bg-gray-200 text-gray-700"}`}
                >
                  {newsArticle.trending ? "✓ Trending" : "☐ Trending"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-2 p-4 bg-white">
          {/* Title - editable on click */}
          {editingField === "title" ? (
            <div className="mb-2">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="w-full p-2 text-2xl font-bold border border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <div className="flex space-x-2 mt-1">
                <button
                  onClick={saveEdit}
                  className="px-2 py-1 text-xs bg-green-600 text-white rounded"
                >
                  Save
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-2 py-1 text-xs bg-gray-500 text-white rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <h1
              className="text-2xl font-bold mb-2 px-2 py-1 hover:bg-gray-100 rounded cursor-pointer"
              onClick={() => startEditing("title", newsArticle.title)}
            >
              {newsArticle.title}
            </h1>
          )}

          {/* Subtitle - editable on click */}
          {editingField === "subtitle" ? (
            <div className="mb-2">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="w-full p-2 text-xl text-gray-700 border border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <div className="flex space-x-2 mt-1">
                <button
                  onClick={saveEdit}
                  className="px-2 py-1 text-xs bg-green-600 text-white rounded"
                >
                  Save
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-2 py-1 text-xs bg-gray-500 text-white rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <h2
              className="text-xl text-gray-700 mb-2 px-2 py-1 hover:bg-gray-100 rounded cursor-pointer"
              onClick={() => startEditing("subtitle", newsArticle.subtitle)}
            >
              {newsArticle.subtitle}
            </h2>
          )}

          {/* Summary - editable on click */}
          {editingField === "summary" ? (
            <div className="mb-4">
              <textarea
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                rows={4}
                className="w-full p-2 text-gray-600 border border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <div className="flex space-x-2 mt-1">
                <button
                  onClick={saveEdit}
                  className="px-2 py-1 text-xs bg-green-600 text-white rounded"
                >
                  Save
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-2 py-1 text-xs bg-gray-500 text-white rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p
              className="text-gray-600 mb-4 px-2 py-1 hover:bg-gray-100 rounded cursor-pointer"
              onClick={() => startEditing("summary", newsArticle.summary)}
            >
              {newsArticle.summary}
            </p>
          )}

          {/* Article content preview */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">Article Content</h3>
            <TipTap
              content={newsArticle.content}
              onChange={(content) => setNewsArticle(prev => ({ ...prev, content }))}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep; 